import { useRef, useState } from 'react';
import { Box, IconButton, Paper, Popper, useTheme } from '@mui/material';
import { IconInfoCircle } from '@tabler/icons-react';
import { useEngineStrings } from '../../user_guides/i18n/useEngineStrings';
import { ENTRY_TYPES, entryTypeLabelKey } from '../../utils/entryType';

/**
 * Shared Entry Type help (#1938): a circle-i that shows the definitions table
 * from the epic (Entry Type by Debit and Credit, four rows, one footer line)
 * on hover, focus or click; a click pins it. An optional `label` renders
 * before `children` and triggers the help too; `children` (the Segmented in
 * EntryTypeControl) does not. One component, one copy of the wording: the
 * in-place control, the part 7 flyout and the Payees page header all use it.
 * The table floats on the 2.7 overlay surface.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.label]     text before the children, a trigger
 * @param {React.ReactNode} [props.children]  content between label and icon
 * @param {object} [props.sx]                 extra sx merged onto the wrapper
 */
const EntryTypeHelp = ({ label, children, sx }) => {
  const t = useEngineStrings();
  const theme = useTheme();
  const { ds, palette } = theme;
  const anchorRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hover || focus || pinned;
  const trigger = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
  };
  const cellSx = {
    fontSize: '12px',
    lineHeight: 1.35,
    color: palette.text.secondary,
    padding: '5px 12px 5px 0',
    borderBottom: `1px solid ${palette.divider}`,
    textAlign: 'left',
    verticalAlign: 'top',
    fontWeight: ds.font.weightRegular,
  };

  return (
    <Box ref={anchorRef} sx={{ display: 'inline-flex', alignItems: 'center', gap: '10px', ...sx }}>
      {label != null && (
        <Box
          component="span"
          {...trigger}
          sx={{ fontSize: '13px', color: palette.text.primary, whiteSpace: 'nowrap', cursor: 'help' }}
        >
          {label}
        </Box>
      )}
      {children}
      <IconButton
        size="small"
        aria-label={t['entryType.help.aria']}
        aria-expanded={open}
        {...trigger}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onClick={() => setPinned((p) => !p)}
        sx={{
          width: 22,
          height: 22,
          padding: 0,
          color: open ? palette.interactive : palette.text.secondary,
          '&:hover': { backgroundColor: 'transparent', color: palette.interactive },
        }}
      >
        <IconInfoCircle size={16} />
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
        sx={{ zIndex: theme.zIndex.tooltip }}
      >
        <Paper
          sx={{
            backgroundColor: palette.surface.overlay,
            backgroundImage: 'none',
            border: `1px solid ${palette.overlay.border}`,
            boxShadow: palette.overlay.shadow,
            borderRadius: `${ds.radius.container}px`,
            padding: '6px 10px 8px',
            width: 520,
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
            <Box component="thead">
              <Box component="tr">
                {[t['entryType.label'], t['entryType.help.debit'], t['entryType.help.credit']].map((head) => (
                  <Box
                    component="th"
                    key={head}
                    sx={{ ...cellSx, fontSize: '11px', letterSpacing: '.04em', textTransform: 'uppercase', color: palette.text.secondary, fontWeight: ds.font.weightMedium }}
                  >
                    {head}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {ENTRY_TYPES.map((type, i) => (
                <Box component="tr" key={type}>
                  <Box component="td" sx={{ ...cellSx, color: palette.text.primary, fontWeight: ds.font.weightMedium, width: 78, ...(i === ENTRY_TYPES.length - 1 ? { borderBottom: 0 } : {}) }}>
                    {t[entryTypeLabelKey(type)]}
                  </Box>
                  <Box component="td" sx={{ ...cellSx, ...(i === ENTRY_TYPES.length - 1 ? { borderBottom: 0 } : {}) }}>
                    {t[`entryType.help.${type}.debit`]}
                  </Box>
                  <Box component="td" sx={{ ...cellSx, paddingRight: 0, ...(i === ENTRY_TYPES.length - 1 ? { borderBottom: 0 } : {}) }}>
                    {t[`entryType.help.${type}.credit`]}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ fontSize: '11px', color: palette.text.secondary, paddingTop: '6px', borderTop: `1px solid ${palette.divider}`, marginTop: '2px' }}>
            {t['entryType.help.footer']}
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
};

export default EntryTypeHelp;
